import { useState } from "react";
import { AllergyDto, allergyFormData, allergyKeyId } from "../types/allergy";
import { allergyService } from "../services/allergyService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useAllergies = () => {
  // 1. Το GET γίνεται αυτόματα με το useApi και εκμεταλλευόμαστε την cache (π.χ. κλειδί "allergies")
  const {
    data: allergies = [],
    loading: isFetching,
    error: getError,
    refetch: fetchAllergies,
    invalidateCache,
  } = useApi<AllergyDto[]>("allergies", (signal) => allergyService.getAll({}, signal));

  // 2. Το CREATE τυλίγεται στο καθαρό useMutation με τον σωστό τύπο δεδομένων φόρμας
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newAllergy: typeof allergyFormData) => allergyService.create(newAllergy));

// Για το UPDATE στο hook σου:
const {
  execute: updateExecution,
  loading: isUpdating,
  error: updateError,
} = useMutation(({ id, data }: { id: number | string; data: AllergyDto }) =>
  allergyService.update(id, data)
);

  // 3. Το DELETE τυλίγεται επίσης στο useMutation
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => allergyService.delete(id)));
    return ids;
  });

  // State αποκλειστικά για το ErrorSuccessModal
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createAllergy = async (newAllergy: typeof allergyFormData): Promise<void> => {
    try {
      await createExecution(newAllergy);
      
      // Καθαρίζουμε την cache για να ξανατραβήξει φρέσκα δεδομένα στην επόμενη προβολή
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η αλλεργία αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchAllergies(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updateAllergies = async (id: number | string, updatedData: AllergyDto): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchAllergies(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteAllergies = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      
      // Καθαρίζουμε την cache
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchAllergies(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    allergies,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchAllergies: () => fetchAllergies(true),
    createAllergy,
    updateAllergies,
    deleteAllergies,
    modalState,
    closeModal,
  };
};